import { Image as ImageIcon, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Button } from "@/shared/ui/button";

import { StatusBadge } from "./StatusBadge";
import type { TemplateListItem } from "../api";

interface Props {
  item: TemplateListItem;
  onDelete: (id: string) => void;
  disabled?: boolean;
}

function formatDate(iso: string): string {
  try {
    return format(new Date(iso), "dd/MM/yy", { locale: ptBR });
  } catch {
    return "";
  }
}

export function TemplateListCard({ item, onDelete, disabled }: Props) {
  return (
    <div className="group relative border rounded-lg bg-card overflow-hidden flex flex-col hover:border-primary/50 transition-colors">
      <Link to={`/psd-templates/${item.id}`} className="block">
        <div className="relative aspect-[3/4] bg-muted overflow-hidden">
          {item.preview ? (
            <img
              src={item.preview}
              alt={item.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ImageIcon className="w-8 h-8 opacity-30" />
            </div>
          )}
          <div className="absolute top-1.5 left-1.5">
            <StatusBadge status={item.status} />
          </div>
        </div>
        <div className="p-2 space-y-0.5">
          <h3
            className="text-xs font-medium text-foreground truncate leading-snug"
            title={item.name}
          >
            {item.name}
          </h3>
          <p className="text-[10px] text-muted-foreground flex items-center gap-1 truncate">
            {item.width && item.height ? (
              <span>
                {item.width}×{item.height}
              </span>
            ) : (
              <span>—</span>
            )}
            <span className="opacity-40">·</span>
            <span>{formatDate(item.createdAt)}</span>
          </p>
        </div>
      </Link>
      <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="icon"
          variant="destructive"
          className="h-6 w-6"
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(item.id);
          }}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
